// src/components/CurrencyRateWidget.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Spin, Tooltip, Typography } from 'antd';
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text } = Typography;

const API_URL = 'https://cb.am/latest.json.php';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour (cb.am updates once daily)

interface RateCache {
  rate: number;
  fetchedAt: number;
}

let cache: RateCache | null = null;

async function fetchUsdAmd(): Promise<number> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.rate;
  }
  const res = await fetch(API_URL);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const rate = parseFloat(json?.USD);
  if (!rate || !Number.isFinite(rate)) throw new Error('USD rate not found in response');
  cache = { rate, fetchedAt: Date.now() };
  return rate;
}

const CurrencyRateWidget: React.FC = () => {
  const [open, setOpen]       = useState(false);
  const [rate, setRate]       = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const [usdInput, setUsdInput] = useState('');
  const [amdInput, setAmdInput] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const loadRate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchUsdAmd();
      setRate(r);
    } catch (e: any) {
      setError('Не удалось загрузить курс. Проверьте интернет.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRate();
    return () => abortRef.current?.abort();
  }, [open, loadRate]);

  const handleUsdChange = (v: string) => {
    setUsdInput(v);
    if (rate && v !== '') {
      const n = parseFloat(v);
      setAmdInput(Number.isFinite(n) ? (n * rate).toFixed(0) : '');
    } else {
      setAmdInput('');
    }
  };

  const handleAmdChange = (v: string) => {
    setAmdInput(v);
    if (rate && v !== '') {
      const n = parseFloat(v);
      setUsdInput(Number.isFinite(n) ? (n / rate).toFixed(2) : '');
    } else {
      setUsdInput('');
    }
  };

  const handleOpen = () => {
    setUsdInput('');
    setAmdInput('');
    setOpen(true);
  };

  return (
    <>
      <Tooltip title="Курс валют USD / AMD">
        <Button
          type="text"
          icon={<DollarOutlined />}
          className="header-icon"
          onClick={handleOpen}
          aria-label="Курс валют"
        />
      </Tooltip>

      <Modal
        title="Курс валют USD / AMD"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={340}
        destroyOnHidden
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Text type="danger">{error}</Text>
            <br />
            <Button
              icon={<ReloadOutlined />}
              style={{ marginTop: 12 }}
              onClick={loadRate}
            >
              Повторить
            </Button>
          </div>
        )}

        {rate && !loading && !error && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>
                1 USD = {rate.toFixed(2)} AMD
              </Text>
              <Tooltip title="Обновить курс">
                <Button
                  size="small"
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={() => { cache = null; loadRate(); }}
                />
              </Tooltip>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Input
                addonBefore="USD"
                placeholder="0.00"
                value={usdInput}
                onChange={(e) => handleUsdChange(e.target.value)}
                type="number"
                min={0}
              />
              <Input
                addonBefore="AMD"
                placeholder="0"
                value={amdInput}
                onChange={(e) => handleAmdChange(e.target.value)}
                type="number"
                min={0}
              />
            </div>

            <Text type="secondary" style={{ fontSize: 11, textAlign: 'right' }}>
              Источник: ЦБ Армении (cb.am) · обновляется раз в сутки
            </Text>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CurrencyRateWidget;
